import { useState } from "react";
import { MapPin } from "lucide-react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import "mapbox-gl/dist/mapbox-gl.css";

interface MiniMapDialogProps {
  latitude: number;
  longitude: number;
  deviceName: string;
  thumbnailUrl?: string;
}

export const MiniMapDialog = ({ latitude, longitude, deviceName, thumbnailUrl }: MiniMapDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleOpenMap = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Fetch Mapbox token when opening
    if (!mapboxToken) {
      try {
        const { data, error } = await supabase.functions.invoke('getMapboxToken');
        if (error) throw error;
        if (data?.token) {
          setMapboxToken(data.token);
        }
      } catch (error) {
        console.error('Error fetching Mapbox token:', error);
        toast.error('Failed to load map');
        return;
      }
    }
    
    setIsOpen(true);
  };

  // Validate coordinates
  const isValidCoordinate = latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

  const mapContent = (
    <div className="w-full h-[400px] sm:h-[70vh] rounded-md overflow-hidden">
      {!isValidCoordinate ? (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <p className="text-muted-foreground">Location not available</p>
        </div>
      ) : !mapboxToken ? (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      ) : (
        <Map
          mapboxAccessToken={mapboxToken}
          initialViewState={{
            longitude,
            latitude,
            zoom: 14
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
        >
          <NavigationControl position="top-right" />
          <Marker longitude={longitude} latitude={latitude}>
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={deviceName}
                className="w-10 h-10 rounded-full border-2 border-background shadow-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="showcase-map-marker-fallback w-10 h-10 rounded-full bg-primary flex items-center justify-center border-2 border-background shadow-lg">
                <MapPin className="h-5 w-5 text-primary-foreground" />
              </div>
            )}
          </Marker>
        </Map>
      )}
    </div>
  );

  const content = isMobile ? (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="showcase-dialog">
        <DrawerHeader>
          <DrawerTitle>{deviceName} Location</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-4">
          {mapContent}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="showcase-dialog max-w-2xl">
        <DialogHeader>
          <DialogTitle>{deviceName} Location</DialogTitle>
        </DialogHeader>
        {mapContent}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <button
        type="button"
        onClick={handleOpenMap}
        className="showcase-image-action absolute bottom-2 left-2 z-40 bg-foreground/70 text-background p-1.5 rounded-full hover:bg-foreground/90 transition-colors shadow-lg pointer-events-auto"
        aria-label="View device location on map"
      >
        <MapPin className="h-4 w-4" />
      </button>
      {content}
    </>
  );
};
